import { useState, useRef, useMemo, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    Chip,
    Stack,
} from "@mui/material";
import { CloudUpload, Warning } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import Papa from "papaparse";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { usePost } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import {
    showSuccessSnackbar,
    showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

// Maps normalized (trimmed + uppercased) CSV headers to API field names.
// Organization / Cohort / Team are intentionally excluded; those come from the
// dropdowns so we don't depend on free-text spreadsheet values matching the DB.
const HEADER_MAP = {
    FIRST: "first_name",
    LAST: "last_name",
    "STUDENT EMAIL": "email",
    "ACT SCORE": "start_composite",
    "ACT ENGLISH": "start_english",
    "ACT READING": "start_reading",
    "ACT MATH": "start_math",
    "ACT SCIENCE": "start_science",
    PASSWORD: "password",
    "GOAL SCORE": "goal_score",
    "ENROLLMENT STEP": "enrollment_step",
    "ENROLLMENT COMPLETED": "enrollment_completed",
    "PROGRAM TYPE": "program_type",
    "CLASS DAY": "class_day",
    "START SCORE TYPE": "start_scores_type",
    TIMEZONE: "timezone",
    "PLANNED NUMBER OF MOCK ACTS": "planned_number_of_mock_acts",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mapRow(rawRow) {
    const mapped = {};
    Object.entries(rawRow).forEach(([key, value]) => {
        const normalizedKey = String(key).trim().toUpperCase();
        const field = HEADER_MAP[normalizedKey];
        if (field) {
            mapped[field] = typeof value === "string" ? value.trim() : value;
        }
    });
    return mapped;
}

function validateRow(row) {
    const errors = {};
    if (!row.first_name) errors.first_name = "Missing first name";
    if (!row.last_name) errors.last_name = "Missing last name";
    if (!row.email) {
        errors.email = "Missing email";
    } else if (!EMAIL_REGEX.test(row.email)) {
        errors.email = "Invalid email";
    }
    if (!row.password) errors.password = "Missing password";
    return errors;
}

export default function StudentImportDialog({ open, onClose, onSuccess }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [students, setStudents] = useState([]);
    const [parseError, setParseError] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [cohort, setCohort] = useState(null);
    const [team, setTeam] = useState(null);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);

    const importMutation = usePost(apiUrls.admin.bulkStudentImport());

    const validationErrors = useMemo(() => {
        const map = {};
        students.forEach((row) => {
            const rowErrors = validateRow(row);
            if (Object.keys(rowErrors).length > 0) {
                map[row.id] = rowErrors;
            }
        });
        return map;
    }, [students]);

    const totalErrorCount = useMemo(
        () => Object.keys(validationErrors).length,
        [validationErrors],
    );

    const resetState = useCallback(() => {
        setSelectedFile(null);
        setStudents([]);
        setParseError(null);
        setOrganization(null);
        setCohort(null);
        setTeam(null);
        setResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onClose();
    }, [resetState, onClose]);

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
            setParseError("Please select a CSV file.");
            return;
        }

        setSelectedFile(file);
        setParseError(null);
        setResult(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const mapped = results.data
                    .map((row, index) => ({ id: index, ...mapRow(row) }))
                    .filter(
                        (row) => row.email || row.first_name || row.last_name,
                    );
                if (mapped.length === 0) {
                    setParseError(
                        "No student rows were found. Check that the CSV has the expected column headers.",
                    );
                }
                setStudents(mapped);
            },
            error: () => {
                setParseError(
                    "Failed to parse the CSV file. Please check the format.",
                );
            },
        });
    };

    const handleSubmit = async () => {
        const payload = {
            organization_uuid: organization?.uuid,
            cohort_uuid: cohort?.uuid,
            team_uuid: team?.uuid || null,
            students: students.map((row) => {
                const rest = { ...row };
                delete rest.id;
                return rest;
            }),
        };

        try {
            const response = await importMutation.mutateAsync(payload);
            const { data } = response;
            if (!data.success) {
                showErrorSnackbar(data.reason || "Import failed.");
                return;
            }
            setResult({
                created: data.created,
                updated: data.updated,
                errors: data.errors || [],
            });
            showSuccessSnackbar(
                `Imported ${data.created} new and updated ${data.updated} students.`,
            );
            if (onSuccess) onSuccess();
        } catch (err) {
            showErrorSnackbar("An unexpected error occurred during import.");
        }
    };

    const columns = useMemo(() => {
        const errorCell = (field) => (params) => {
            const fieldError = validationErrors[params.row.id]?.[field];
            return (
                <Typography
                    sx={{
                        color: fieldError ? "error.main" : "inherit",
                        fontWeight: fieldError ? "bold" : "normal",
                    }}
                    title={fieldError || ""}
                >
                    {params.value || (fieldError ? "—" : "")}
                </Typography>
            );
        };

        return [
            {
                field: "first_name",
                headerName: "First",
                width: 120,
                renderCell: errorCell("first_name"),
            },
            {
                field: "last_name",
                headerName: "Last",
                width: 120,
                renderCell: errorCell("last_name"),
            },
            {
                field: "email",
                headerName: "Email",
                width: 240,
                renderCell: errorCell("email"),
            },
            {
                field: "password",
                headerName: "Password",
                width: 120,
                renderCell: errorCell("password"),
            },
            { field: "start_composite", headerName: "ACT", width: 70 },
            { field: "start_english", headerName: "Eng", width: 60 },
            { field: "start_math", headerName: "Math", width: 60 },
            { field: "start_reading", headerName: "Read", width: 60 },
            { field: "start_science", headerName: "Sci", width: 60 },
            { field: "goal_score", headerName: "Goal", width: 70 },
            { field: "class_day", headerName: "Class Day", width: 110 },
            {
                field: "start_scores_type",
                headerName: "Score Type",
                width: 110,
            },
            { field: "timezone", headerName: "Timezone", width: 150 },
            { field: "program_type", headerName: "Program", width: 110 },
            {
                field: "planned_number_of_mock_acts",
                headerName: "Mock ACTs",
                width: 100,
            },
            { field: "enrollment_step", headerName: "Enr. Step", width: 90 },
            {
                field: "enrollment_completed",
                headerName: "Enr. Done",
                width: 100,
            },
        ];
    }, [validationErrors]);

    const canSubmit =
        students.length > 0 &&
        totalErrorCount === 0 &&
        !!organization &&
        !!cohort &&
        !importMutation.isLoading &&
        !result;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
            <DialogTitle>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Typography variant='h6'>
                        Import Students from CSV
                    </Typography>
                    {students.length > 0 && (
                        <Chip
                            label={`${students.length} student${
                                students.length > 1 ? "s" : ""
                            }`}
                            color='primary'
                            size='small'
                        />
                    )}
                    {totalErrorCount > 0 && (
                        <Chip
                            label={`${totalErrorCount} row${
                                totalErrorCount > 1 ? "s" : ""
                            } with errors`}
                            color='error'
                            size='small'
                        />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <ModelAutoComplete
                            modelName='Organization'
                            label='Organization'
                            value={organization}
                            setValue={setOrganization}
                            required
                            sx={{ flex: 1 }}
                        />
                        <ModelAutoComplete
                            modelName='Cohort'
                            label='Cohort'
                            value={cohort}
                            setValue={setCohort}
                            required
                            sx={{ flex: 1 }}
                        />
                        <ModelAutoComplete
                            modelName='SecondaryTeam'
                            label='Team (optional)'
                            value={team}
                            setValue={setTeam}
                            sx={{ flex: 1 }}
                        />
                    </Stack>

                    {parseError && (
                        <Alert
                            severity='error'
                            onClose={() => setParseError(null)}
                        >
                            {parseError}
                        </Alert>
                    )}

                    {result && (
                        <Alert
                            severity={
                                result.errors.length > 0 ? "warning" : "success"
                            }
                        >
                            <Typography
                                variant='body2'
                                sx={{ fontWeight: "bold" }}
                            >
                                {result.created} created, {result.updated}{" "}
                                updated
                                {result.errors.length > 0
                                    ? `, ${result.errors.length} failed`
                                    : ""}
                                .
                            </Typography>
                            {result.errors.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        maxHeight: 160,
                                        overflow: "auto",
                                    }}
                                >
                                    {result.errors.map((e, idx) => (
                                        <Typography
                                            key={idx}
                                            variant='caption'
                                            component='div'
                                            color='error'
                                        >
                                            Row {e.row} ({e.email}): {e.error}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Alert>
                    )}

                    {!result && (
                        <Button
                            component='label'
                            variant='outlined'
                            startIcon={<CloudUpload />}
                            fullWidth
                        >
                            {selectedFile
                                ? `Selected: ${selectedFile.name}`
                                : "Upload CSV File"}
                            <VisuallyHiddenInput
                                ref={fileInputRef}
                                type='file'
                                accept='.csv,text/csv'
                                onChange={handleFileSelect}
                            />
                        </Button>
                    )}

                    {totalErrorCount > 0 && (
                        <Alert severity='warning' icon={<Warning />}>
                            Fix the highlighted rows before importing. Rows
                            missing a name, email, or password cannot be
                            submitted.
                        </Alert>
                    )}

                    {students.length > 0 && (
                        <Box sx={{ height: 420, width: "100%" }}>
                            <DataGridPro
                                rows={students}
                                columns={columns}
                                disableColumnMenu
                                hideFooter={students.length <= 100}
                                density='compact'
                                getRowClassName={(params) =>
                                    validationErrors[params.id]
                                        ? "row-error"
                                        : ""
                                }
                                sx={{
                                    "& .row-error": {
                                        backgroundColor:
                                            "rgba(244, 67, 54, 0.08)",
                                    },
                                    "& .MuiDataGrid-columnHeader": {
                                        height: "unset !important",
                                    },
                                    "& .MuiDataGrid-columnHeaders": {
                                        maxHeight: "none !important",
                                    },
                                    "& .MuiDataGrid-columnHeaderTitle": {
                                        whiteSpace: "normal",
                                        lineHeight: 1.2,
                                        textOverflow: "clip",
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {students.length === 0 && !parseError && (
                        <Typography variant='body2' color='text.secondary'>
                            Expected columns: First, Last, Student Email,
                            Password, ACT Score, ACT English, ACT Reading, ACT
                            Math, ACT Science, GOAL SCORE, ENROLLMENT STEP,
                            ENROLLMENT COMPLETED, PROGRAM TYPE, CLASS DAY, START
                            SCORE TYPE, TIMEZONE, PLANNED NUMBER OF MOCK ACTS.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {result ? "Close" : "Cancel"}
                </Button>
                {!result && (
                    <Button
                        variant='contained'
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {importMutation.isLoading
                            ? "Importing…"
                            : `Import ${students.length || ""} Student${
                                  students.length === 1 ? "" : "s"
                              }`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
